import * as React from "react";
import * as moment from "moment";
import * as _ from "lodash";
import {lazyInject} from "@athena-portal/core";
import {
    ICustomerPortalService,
    resourcesHelper,
    Types,
    CoverChange
} from "@falcon/services";
import {getCoverTierWording} from "../../../../../CommonFunctions";

interface IState {
    isLoading: boolean;
    coverChanges: CoverChange[];
}

interface IProps {
    policyNumber: string,
    policyEndDate: string,
    upcomingMtaDate?: string;
}

export class FutureMTAExplainedHeader extends React.Component<IProps, IState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: any) {
        super(props);

        this.state = {
            isLoading: true,
            coverChanges: []
        };
    }

    public async componentDidMount() {
        this.setState({isLoading: true});
        this.customerPortalService.getCoverChanges(this.props.policyNumber).subscribe(
            (coverChanges: CoverChange[]) => {
                this.setState({
                    coverChanges,
                    isLoading: false
                })
            }
        )
    }

    public render() {
        if (!this.state.isLoading && this.props.upcomingMtaDate && this.state.coverChanges) {
            const mtaOnEndDateOfPolicy: boolean = moment.utc(this.props.policyEndDate).isSame(moment.utc(this.props.upcomingMtaDate), 'day');
            const additionalDescription: any = mtaOnEndDateOfPolicy ?
                (<>
                    You can't make any additional changes to this policy as the existing change date is your policy
                    expiry date.
                    <br/>
                    If you have renewed your policy you can make a change to your new policy from its policy start date
                </>) :
                "The earliest opportunity to make additional changes is " + this.getNextPossibleMtaDate(this.props.upcomingMtaDate!, "DD/MM/YYYY")
            if (this.state.coverChanges) {
                return (
                    <div className="banner banner-info banner-info-multiple-paragraphs">
                        <p className="grid-col-2">Heads up - you have made changes to take effect from future dates:</p>
                        {this.generateCoverChangeText(this.state.coverChanges)}
                        <div className="grid-col-2 margin-top-16"><strong>{additionalDescription}</strong></div>
                    </div>
                );
            } else {
                return (
                    <div className="banner">
                        <img src={`/${resourcesHelper.THEME}/img/alert-blue.svg`} alt="Blue alert icon"/>
                        <div className="banner-text">
                            <p><strong>You have a future change that takes effect
                                on {this.convertDate(this.props.upcomingMtaDate, "DD/MM/YYYY")}</strong>
                            </p>
                            <p>{additionalDescription}</p>
                        </div>
                    </div>
                );
            }
        } else {
            return <></>;
        }
    }

    public generateCoverChangeText(coverChanges: CoverChange[]): JSX.Element {
        const groupedByStartDate = _.groupBy(coverChanges, "startDate");

        let textLines: string[] = [];

        Object.entries(groupedByStartDate).forEach(([startDate, changes]) => {
            const textLinesCoverTier = this.transformCoverTierIntoTextLines(changes);
            const textLinesOptionalExtras = this.transformOptionalExtrasIntoTextLines(changes);
            const textLinesMTA = this.transformMTAChangeIntoTextLines(changes);

            const coverChangesText = textLinesCoverTier.concat(textLinesOptionalExtras, textLinesMTA).join(", ");
            const line = this.convertDate(startDate, "DD/MM/YYYY") + ": " + _.upperFirst(coverChangesText);

            textLines.push(line);
        })

        const output = textLines.map((line: string, index) =>
            <li className="margin-bottom-8" key={index}>{line}</li>
        );
        return (<ul className="grid-col-2 m-0">{output}</ul>);
    }

    public transformCoverTierIntoTextLines(coverChanges: CoverChange[]) {
        return coverChanges
            .filter(coverChange => coverChange.type === "COVER_TIER")
            .map(coverChange =>
                `upgraded cover tier from ${getCoverTierWording(coverChange.previousValue)} to ${getCoverTierWording(coverChange.currentValue)}`);
    }

    private transformMTAChangeIntoTextLines(coverChanges: CoverChange[]) {
        return coverChanges
            .filter(coverChange => this.isMTAChange(coverChange))
            .map(coverChange => this.getMtaChangeText(coverChange));
    }

    private getMtaChangeText(coverChange: CoverChange) {
        switch (coverChange.type) {
            case "REPLACE_CAR":
                return `replaced ${coverChange.previousValue}`;
            case "ADD_DRIVER":
                return `added ${coverChange.currentValue}`;
            case "EDIT_DRIVER":
                return `edited ${coverChange.currentValue}`;
            case "REMOVE_DRIVER":
                return `removed ${coverChange.currentValue}`;
            case "CHANGE_ADDRESS":
                return `changed address`;
            case "EDIT_CAR_DETAILS":
                return `edited car details`;
            default:
                return "";
        }
    }

    private transformOptionalExtrasIntoTextLines(coverChanges: CoverChange[]) {
        let optionalExtrasChanges = coverChanges.filter(coverChange => coverChange.type === "OPTIONAL_EXTRA");

        const addedBreakdownCover = optionalExtrasChanges.filter(coverChange =>
            coverChange.action === "ADDED" && this.isBreakdownCover(coverChange.changedField))
        const deletedBreakdownCover = optionalExtrasChanges.filter(coverChange =>
            coverChange.action === "REMOVED" && this.isBreakdownCover(coverChange.changedField))

        let lines: string[] = [];

        if (addedBreakdownCover.length === 1 && deletedBreakdownCover.length === 1) {
            optionalExtrasChanges = optionalExtrasChanges.filter(s => !this.isBreakdownCover(s.changedField));
            lines.push("modified " + this.convertCoverType(deletedBreakdownCover[0].changedField) +
                " to " + this.convertCoverType(addedBreakdownCover[0].changedField))
        }

        this.constructTextLines(optionalExtrasChanges, lines);

        return lines
    }

    private isBreakdownCover(coverType: string) {
        return coverType.startsWith("BREAKDOWN_COVER")
    }

    private isMTAChange(coverChange: CoverChange) {
        const mtaChangeTypes = ["REPLACE_CAR", "ADD_DRIVER", "EDIT_DRIVER", "REMOVE_DRIVER", "CHANGE_ADDRESS", "EDIT_CAR_DETAILS"];
        return mtaChangeTypes.includes(coverChange.type);
    }

    private constructTextLines(optionalExtras: CoverChange[], lines: string[]) {
        optionalExtras.forEach(coverChange => {
            return lines.push(
                (this.convertOptionalExtraAction(coverChange) + " " + this.convertCoverType(coverChange.changedField)).toLowerCase()
            );
        });

        return lines;
    }

    private convertOptionalExtraAction(coverChange: CoverChange) {
        switch (coverChange.action) {
            case "ADDED":
                return "Added";
            case "REMOVED":
                return "Removed";
            default:
                return "";
        }
    }

    private convertCoverType = (coverType: string) => {
        switch (coverType) {
            case "BREAKDOWN_COVER_LOCAL":
                return "Breakdown cover (local)";
            case "BREAKDOWN_COVER_UK":
                return "Breakdown cover (national)";
            case "BREAKDOWN_COVER_EU":
                return "Breakdown cover (european)";
            case "FAMILY_PACKAGE":
                return "Family package";
            case "PROTECTED_NCD":
                return "Protected no claim discount";
            case "COURTESY_CAR":
                return "Guaranteed Hire Car";
            case "LEGAL_EXPENSES":
                return "Motor legal expenses";
            default:
                throw new Error(`Unknown cover type.`);
        }
    }

    private convertDate = (inputDate: string, format: string) => {
        const valueAsMoment = moment.utc(inputDate, moment.ISO_8601);
        if (valueAsMoment.isValid()) {
            return (valueAsMoment.format(format))
        }
        return null;
    };

    private getNextPossibleMtaDate = (inputDate: string, format: string) => {
        const valueAsMoment = moment.utc(inputDate, moment.ISO_8601);
        if (valueAsMoment.isValid()) {
            return (valueAsMoment.add(1, "days").format(format))
        }
        return null;
    };
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/annual/motor/yourCarsPage/FutureMTAExplainedHeader.tsx